import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function EmailTextsEditor() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/email-texts").then((r) => setItems(r.data || [])).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="card mt-5" data-testid="email-texts-editor">
      <h2 className="text-xl font-bold font-display flex items-center gap-2 mb-1">
        <EnvelopeSimple size={22} weight="duotone" color="#CCFF00" /> French emails — edit the wording
      </h2>
      <p className="text-sm text-[#000000] mb-3">
        The placeholders in curly braces are filled in automatically for each recipient — keep them where you want that info to appear.
      </p>
      {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
      {items.map((it) => <EmailCard key={it.key} it={it} onErr={setErr} onReload={load} />)}
    </div>
  );
}

function EmailCard({ it, onErr, onReload }) {
  const [subject, setSubject] = useState(it.subject);
  const [html, setHtml] = useState(it.html);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setSubject(it.subject); setHtml(it.html); }, [it]);
  const dirty = subject !== it.subject || html !== it.html;

  const save = async () => {
    onErr(""); setSaved(false);
    try {
      await api.put(`/admin/email-texts/${it.key}`, { subject, html });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
      onReload();
    } catch (e) { onErr(formatApiError(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this email to the original wording?")) return;
    try { await api.delete(`/admin/email-texts/${it.key}`); onReload(); } catch (e) { onErr(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl border border-[#C4C8CE] bg-white p-4 mb-3" data-testid={`email-text-${it.key}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <div className="font-bold">{it.label}</div>
        {it.is_override && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[rgba(0,184,212,0.14)] text-[#00768a]">customized</span>}
        <div className="ml-auto flex items-center gap-1.5">
          {it.tokens.map((t) => <code key={t} className="text-[10px] bg-[#EEE] px-1.5 py-0.5 rounded">{t}</code>)}
        </div>
      </div>
      <label className="text-xs uppercase tracking-[0.12em] font-semibold">Subject</label>
      <input className="input-base w-full text-sm mb-2" value={subject} data-testid={`email-subject-${it.key}`}
        onChange={(e) => setSubject(e.target.value)} />
      <label className="text-xs uppercase tracking-[0.12em] font-semibold">Body (HTML)</label>
      <textarea className="input-base w-full text-xs font-mono" rows={12} value={html} data-testid={`email-html-${it.key}`}
        onChange={(e) => setHtml(e.target.value)} />
      <div className="flex items-center gap-2 mt-2">
        <button className="btn-primary text-sm" disabled={!dirty} onClick={save} data-testid={`email-save-${it.key}`}>Save</button>
        {it.is_override && <button className="btn-ghost text-sm font-semibold" onClick={reset}>Reset to original</button>}
        {saved && <span className="text-[#1a7f37] text-sm flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Saved</span>}
      </div>
    </div>
  );
}
